import { useMemo, useState } from 'react'
import clsx from 'clsx'
import { AlertTriangle, CheckCircle2, Info, ShieldAlert, ShieldCheck, Route as RouteIcon, FileText } from 'lucide-react'
import { useSpatialStore } from '@/features/spatial-model/store.js'
import { runValidation } from '@/features/validation/rules.js'
import RouteControls from './RouteControls.jsx'
import WorkspaceInspector from './WorkspaceInspector.jsx'

const TABS = [
  { id: 'inspector', label: 'Inspector' },
  { id: 'validation', label: 'Validation' },
  { id: 'route', label: 'Route preview' },
  { id: 'notes', label: 'Notes' },
]

export default function WorkspaceBottom() {
  const tab = useSpatialStore((s) => s.bottomTab)
  const setTab = useSpatialStore((s) => s.setBottomTab)
  const validation = useSpatialStore((s) => s.validation)
  const routePreview = useSpatialStore((s) => s.routePreview)
  const graph = useSpatialStore((s) => s.graph)
  const floor = useSpatialStore((s) => s.floor)
  const setValidation = useSpatialStore((s) => s.setValidation)

  const [validationRun, setValidationRun] = useState(false)
  const validate = () => {
    setValidation(runValidation(graph, floor))
    setValidationRun(true)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="bottom-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={clsx('bottom-tab', tab === t.id && 'is-active')}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="bottom-pane">
        {tab === 'inspector' && <WorkspaceInspector />}
        {tab === 'validation' && (
          <ValidationPane validation={validation} onValidate={validate} hasRun={validationRun} />
        )}
        {tab === 'route' && <RoutePane route={routePreview} graph={graph} />}
        {tab === 'notes' && (
          <div className="muted">
            <FileText size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />
            Notes appear here when added to the floor.
          </div>
        )}
      </div>
    </div>
  )
}

function ValidationPane({ validation, onValidate, hasRun }) {
  const counts = useMemo(() => ({
    blockers: validation.blockers.length,
    warnings: validation.warnings.length,
    info: validation.info.length,
  }), [validation])
  const total = counts.blockers + counts.warnings + counts.info
  const passed = hasRun && total === 0

  return (
    <div className="col gap-12">
      <div
        className="row"
        style={{
          gap: 12,
          flexWrap: 'wrap',
          paddingBottom: 12,
          borderBottom: '1px solid var(--border)',
        }}
      >
        <button type="button" className="btn btn--sm" onClick={onValidate}>
          <ShieldCheck size={14} /> Validate
        </button>
        <span className="muted" style={{ fontSize: 12 }}>
          Run validation to check graph completeness, anchors, and connectivity.
        </span>
      </div>
      <div className="row">
        <span className="badge badge--err">{counts.blockers} blockers</span>
        <span className="badge badge--warn">{counts.warnings} warnings</span>
        <span className="badge">{counts.info} info</span>
      </div>
      <div className="col">
        {validation.blockers.map((m) => (
          <Message key={m.code + m.message} kind="err" icon={ShieldAlert} message={m} />
        ))}
        {validation.warnings.map((m) => (
          <Message key={m.code + m.message} kind="warn" icon={AlertTriangle} message={m} />
        ))}
        {validation.info.map((m) => (
          <Message key={m.code + m.message} kind="info" icon={Info} message={m} />
        ))}
        {total === 0 && !passed && (
          <div className="muted">Run validation to see issues.</div>
        )}
        {passed && (
          <div
            className="row"
            style={{
              gap: 10,
              padding: '10px 12px',
              borderRadius: 8,
              background: 'var(--ok-soft)',
              color: 'var(--ok)',
              border: '1px solid var(--ok)',
            }}
          >
            <CheckCircle2 size={16} />
            <div>
              <div style={{ fontWeight: 600 }}>Validation passed</div>
              <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                No blockers, warnings, or info messages.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function Message({ kind, icon: Icon, message }) {
  const cls = kind === 'err' ? 'badge--err' : kind === 'warn' ? 'badge--warn' : ''
  return (
    <div className="list-item">
      <div className="row">
        <Icon size={14} color={kind === 'err' ? 'var(--error)' : kind === 'warn' ? 'var(--warn)' : 'var(--text-muted)'} />
        <div>
          <div style={{ fontWeight: 600 }}>{message.message}</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{message.code}</div>
        </div>
      </div>
      <span className={`badge ${cls}`}>{kind}</span>
    </div>
  )
}

function RoutePane({ route, graph }) {
  const walkthrough = useSpatialStore((s) => s.walkthrough)

  const hasRoute = !!route
  const currentSegment = hasRoute
    ? Math.min(
        route.path.length - 1,
        Math.floor(walkthrough.progress * Math.max(1, route.path.length - 1)),
      )
    : 0
  const distanceWalked = hasRoute ? walkthrough.progress * route.distance : 0
  const speedMps = 1.4 * walkthrough.speed
  const remainingSeconds =
    hasRoute && speedMps > 0
      ? Math.max(0, (route.distance - distanceWalked) / speedMps)
      : 0

  return (
    <div className="col gap-12">
      <div
        className="col gap-8"
        style={{ paddingBottom: 12, borderBottom: '1px solid var(--border)' }}
      >
        <RouteControls />
        <FollowHeightControl />
      </div>

      {!hasRoute ? (
        <div className="muted">
          <RouteIcon size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />
          Pick a start and destination above, then press Play to run an animated walkthrough.
        </div>
      ) : (
        <div className="col">
          <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
            <span className="badge badge--ok">{route.path.length} stops</span>
            <span className="badge">{route.distance.toFixed(1)} m</span>
            <span className="badge">{Math.round(walkthrough.progress * 100)}% · {distanceWalked.toFixed(1)} m walked</span>
            <span className="badge">~{remainingSeconds.toFixed(0)}s left</span>
          </div>
          <ol style={{ marginTop: 8, paddingLeft: 20, color: 'var(--text-secondary)' }}>
            {route.path.map((nodeId, idx) => {
              const node = graph.nodes.find((n) => n.id === nodeId)
              const isCurrent = idx === currentSegment
              const isPassed = idx < currentSegment
              return (
                <li
                  key={nodeId + idx}
                  style={{
                    color: isCurrent ? 'var(--accent-strong)' : isPassed ? 'var(--text-muted)' : 'var(--text-secondary)',
                    fontWeight: isCurrent ? 600 : 400,
                    textDecoration: isPassed ? 'line-through' : 'none',
                  }}
                >
                  {node?.name || nodeId.slice(0, 8)} <span className="muted">({node?.node_type || 'unknown'})</span>
                </li>
              )
            })}
          </ol>
        </div>
      )}
    </div>
  )
}

// The FBX scenes we're loading aren't in real-world meters; they come in at
// roughly ~0.38× of true scale. The presets and feet readout below use a
// scene-calibrated foot (~0.118 m) so "5'6"" renders at the height of a real
// person inside the model. If a future model is authored in true meters,
// this calibration is the single value to revisit.
const SCENE_M_PER_FOOT = 0.65 / 5.5

function feetFromMeters(m) {
  return m / SCENE_M_PER_FOOT
}

function FollowHeightControl() {
  const eyeHeightM = useSpatialStore((s) => s.walkthrough.eyeHeightM ?? 0.65)
  const setEyeHeight = useSpatialStore((s) => s.setWalkthroughEyeHeight)

  const feet = feetFromMeters(eyeHeightM)

  return (
    <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <label
        htmlFor="follow-height-input"
        className="muted"
        style={{ fontSize: 12 }}
        title="Camera eye height above the floor in Follow mode"
      >
        Follow height
      </label>
      <input
        id="follow-height-input"
        type="number"
        min={0.1}
        max={3}
        step={0.01}
        value={Number(eyeHeightM.toFixed(2))}
        onChange={(e) => {
          const v = parseFloat(e.target.value)
          if (Number.isFinite(v)) setEyeHeight(v)
        }}
        style={{ width: 84 }}
        aria-label="Follow eye height in scene units"
      />
      <span className="muted" style={{ fontSize: 12 }}>m</span>
      <span className="muted" style={{ fontSize: 12 }}>
        (~{feet.toFixed(1)} ft)
      </span>
      <div className="row" style={{ gap: 4 }}>
        {[
          { label: "4'", feet: 4 },
          { label: "5'6\"", feet: 5.5 },
          { label: "6'", feet: 6 },
        ].map((preset) => (
          <button
            key={preset.label}
            type="button"
            className="btn btn--sm"
            onClick={() => setEyeHeight(preset.feet * SCENE_M_PER_FOOT)}
            title={`Set Follow eye height to ${preset.label}`}
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  )
}
